import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { contractors as contractorsApi } from '@/api/catalogs';
import {
  processingReceipts,
  type ProcessingReceiptListItem,
} from '@/api/processingReceipts';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import ImportedBadge from '@/shared/components/ImportedBadge';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { useOrgNames, agOrgCol } from '@/shared/hooks/useOrgNames';

const PERMISSION = 'production.processing.receipt.manage';

export default function ProcessingReceiptsPage() {
  const navigate = useNavigate();
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const f = useDocListFilters();
  const orgNames = useOrgNames();

  // Серверна пагінація (Норма #29 — InfiniteDocGrid замість client-side limit 500).
  const { datasource, total } = useInfiniteGridDatasource<ProcessingReceiptListItem>(
    (limit, offset, sort) => processingReceipts.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [...f.queryKey],
    { attachmentsType: 'doc.processing_receipts' },
  );

  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const cols = useMemo<ColDef<ProcessingReceiptListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: ProcessingReceiptListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: 'Номер', width: 150, sortable: true },
    {
      field: 'document_date', headerName: 'Дата', width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'processor_contractor_id', headerName: 'Переробник', sortable: true,
      valueFormatter: (p) => (p.value ? cName.get(p.value as string) ?? '' : ''),
    },
    {
      field: 'total_cost', headerName: 'Собівартість продукції', width: 200, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null
        ? Number(p.value).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [cName, orgNames]);

  const canCreate = f.orgId ? allowedOrgIds.has(f.orgId) : perms.availableOrgs.length > 0;
  const tooltipNew = !canCreate
      ? `Немає прав створювати Надходження з переробки у цій юрособі (${PERMISSION})`
      : undefined;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Надходження з переробки<TitleTotal total={total} /></h2>
        <button
            className="primary"
            disabled={!canCreate}
            title={tooltipNew}
            style={{ marginLeft: 'auto' }}
            onClick={() => navigate('/processing-receipts/new')}
          >
            + Нове надходження
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} />
      </DocListFiltersRow>
      <InfiniteDocGrid<ProcessingReceiptListItem>
        deleteRow={{ remove: (id) => processingReceipts.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="processing-receipts"
        onRowDoubleClicked={(r) => navigate(`/processing-receipts/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark
          ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
